import { useCallback, useState } from "react";
import { supabase, SUPABASE_URL } from "../../lib/supabase";
import { fetchEdge, readEdgeJson, sanitizeUserMessage } from "../../lib/edgeError";

/** Reusable cell for showing the SalesRabbit weather report state on a deal.
 *  Three states:
 *    1. No SR lead ID          → disabled chip "no SR link"
 *    2. Has lead ID, no PDF    → "Pull weather report" button (triggers edge fn)
 *    3. Has PDF in Storage      → "View PDF" opens signed URL in new tab  */

interface Props {
  dealId: string;
  salesrabbitLeadId: string | null | undefined;
  storagePath: string | null | undefined;
  fileName: string | null | undefined;
  fileSize: number | null | undefined;
  generated: boolean | null | undefined;
  onUpdated?: () => void; // parent can re-fetch deal after pull
}

export default function WeatherReportCell({
  dealId,
  salesrabbitLeadId,
  storagePath,
  fileName,
  fileSize,
  generated,
  onUpdated,
}: Props) {
  const [busy, setBusy] = useState<"pull" | "view" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pullReport = useCallback(async () => {
    setError(null);
    setBusy("pull");
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) {
        setError("Not signed in");
        return;
      }
      const res = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/sr-pull-weather-report`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ deal_id: dealId }),
        },
      );
      const body = await readEdgeJson<{ ok?: boolean; error?: string }>(res);
      if (!res.ok || body.ok === false) {
        setError(sanitizeUserMessage(body.error, `Pull failed (HTTP ${res.status})`));
        return;
      }
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [dealId, onUpdated]);

  const viewPDF = useCallback(async () => {
    if (!storagePath) return;
    setError(null);
    setBusy("view");
    try {
      const { data, error } = await supabase.storage
        .from("sr-weather-reports")
        .createSignedUrl(storagePath, 300); // 5-minute URL
      if (error || !data?.signedUrl) {
        setError(error?.message ?? "Could not create signed URL");
        return;
      }
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [storagePath]);

  if (!salesrabbitLeadId) {
    return (
      <span
        className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500"
        title="This deal wasn't created from a SalesRabbit lead"
      >
        No SR link
      </span>
    );
  }

  if (storagePath) {
    const sizeMb = fileSize ? (fileSize / 1024 / 1024).toFixed(2) : null;
    return (
      <div className="flex flex-col gap-0.5">
        <button
          onClick={viewPDF}
          disabled={busy === "view"}
          className="text-xs px-2 py-1 rounded bg-bardin-teal text-white hover:bg-slate-700 disabled:opacity-50 text-left"
          title={fileName ?? undefined}
        >
          {busy === "view" ? "Opening…" : "📄 View weather report"}
        </button>
        {sizeMb && (
          <span className="text-[10px] text-slate-400">
            {fileName} · {sizeMb} MB
          </span>
        )}
        {error && (
          <span className="text-[10px] text-red-600 mt-0.5">{error}</span>
        )}
      </div>
    );
  }

  // SR lead exists but no PDF stored yet
  return (
    <div className="flex flex-col gap-0.5">
      <button
        onClick={pullReport}
        disabled={busy === "pull"}
        className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
      >
        {busy === "pull"
          ? "Pulling from SR…"
          : generated
            ? "Re-pull weather report"
            : "Pull weather report"}
      </button>
      {error && (
        <span className="text-[10px] text-red-600 mt-0.5">{error}</span>
      )}
    </div>
  );
}
